// The quota screens' view model (SCR-230 list and editor, SCR-231 standing, grants and events;
// API-230..232, API-237). Pure and DOM-free apart from the reads and writes at the end, which take
// the client as an argument (tests/unit/quotas.test.ts, tests/unit/quota-standing.test.ts);
// rendering lives in the .tsx files beside this one.
//
// Money: every µUSD figure a quota carries (a `spend` quota's limit, consumed, grant amount,
// preview) goes through the org context's money policy (`applyMoneyPolicy`, TASK-240) before it is
// shown, on top of the server's own nulling; a hidden figure renders as a dash and the standing
// falls back to the percentage, which the server always sends.
import type { ApiClient } from "../../api/client";
import { ApiError } from "../../api/errors";
import { applyMoneyPolicy, type MoneyPolicy, type Surface } from "../../api/org-context";
import type { components } from "../../api/schema";
import type { FieldErrors } from "../../primitives/forms";
import { bpToPercentInput, formatUsd, parsePercentToBp, parseUsd } from "../../primitives/money";
import type { ChipTone } from "../../primitives/page";
import type { ScopeMe } from "../../scope/scope";
import { personName, teamName, UNKNOWN_TEAM, UNKNOWN_USER } from "./names";

type Schemas = components["schemas"];
export type Quota = Schemas["Quota"];
export type QuotaDraft = Schemas["QuotaDraft"];
export type PatchQuotaRequest = Schemas["PatchQuotaRequest"];
export type QuotaScope = Schemas["QuotaScope"];
export type QuotaMetric = Schemas["QuotaMetric"];
export type QuotaBasis = Schemas["QuotaBasis"];
export type QuotaPeriod = Schemas["QuotaPeriod"];
export type QuotaEnforcement = Schemas["QuotaEnforcement"];
export type QuotaFailMode = Schemas["QuotaFailMode"];
export type QuotaOwner = Schemas["QuotaOwner"];
export type QuotaState = Schemas["QuotaState"];
export type QuotaGrant = Schemas["QuotaGrant"];
export type QuotaGrantKind = Schemas["QuotaGrantKind"];
export type CreateQuotaGrantRequest = Schemas["CreateQuotaGrantRequest"];
export type QuotaStanding = Schemas["QuotaStanding"];
export type QuotaStandingResponse = Schemas["QuotaStandingResponse"];
export type QuotaEvent = Schemas["QuotaEvent"];
export type QuotaEventList = Schemas["QuotaEventList"];
export type QuotaPreview = Schemas["QuotaPreview"];
export type Team = Schemas["Team"];
export type User = Schemas["User"];

// ---------------------------------------------------------------------------------------------
// Routes (NAV-019): the AllCode admin's per-customer paths; the proxy's Admin area mounts the same
// screens under its own base path (hrefs.ts builds both)

export const QUOTAS_PATH = "/customers/:orgId/quotas";
export const QUOTA_PATH = "/customers/:orgId/quotas/:quotaId";
export const QUOTAS_TITLE = "Quotas";

// ---------------------------------------------------------------------------------------------
// Labels

export const SCOPES: readonly QuotaScope[] = ["org", "team", "org_member", "team_member", "user"];
export const METRICS: readonly QuotaMetric[] = ["spend", "tokens", "requests"];
export const PERIODS: readonly QuotaPeriod[] = ["day", "week", "month"];

const SCOPE_LABEL: Readonly<Record<QuotaScope, string>> = {
  org: "Whole organization",
  team: "One team",
  org_member: "Each member of the organization",
  team_member: "Each member of a team",
  user: "One person",
};
const METRIC_LABEL: Readonly<Record<QuotaMetric, string>> = { spend: "Spend", tokens: "Tokens", requests: "Requests" };
const PERIOD_LABEL: Readonly<Record<QuotaPeriod, string>> = { day: "Daily", week: "Weekly", month: "Monthly" };
const STATE_LABEL: Readonly<Record<QuotaState, string>> = { ok: "OK", warning: "Warning", grace: "In grace", exhausted: "Exhausted", exempt: "Exempt" };
const STATE_TONE: Readonly<Record<QuotaState, ChipTone>> = { ok: "green", warning: "amber", grace: "amber", exhausted: "red", exempt: "blue" };

export const scopeLabel = (scope: QuotaScope): string => SCOPE_LABEL[scope];
export const metricLabel = (metric: QuotaMetric): string => METRIC_LABEL[metric];
export const periodLabel = (period: QuotaPeriod): string => PERIOD_LABEL[period];
export const stateLabel = (state: QuotaState): string => STATE_LABEL[state];
export const stateTone = (state: QuotaState): ChipTone => STATE_TONE[state];

/** Whether the scope names a team (`team`, `team_member`) or a person (`user`). */
export function scopeTarget(scope: QuotaScope): "team" | "user" | null {
  if (scope === "team" || scope === "team_member") return "team";
  return scope === "user" ? "user" : null;
}

/** A per-member scope: a grant there may name one member. */
export function isMemberScope(scope: QuotaScope): boolean {
  return scope === "org_member" || scope === "team_member";
}

/** "75%" for 7500 bp; two decimals only when the value needs them. */
export function bpPercent(bp: number): string {
  return `${bpToPercentInput(bp)}%`;
}

// ---------------------------------------------------------------------------------------------
// Money and amounts

/** A quota amount as the money policy lets this viewer see it: counts pass, µUSD may become null. */
export function visibleAmount(policy: MoneyPolicy, metric: QuotaMetric, basis: QuotaBasis | null, amount: number | null): number | null {
  if (metric !== "spend") return amount;
  const list = basis === "list";
  const shown = applyMoneyPolicy(policy, { costMicroUsd: list ? amount : null, priceMicroUsd: list ? null : amount });
  return list ? shown.costMicroUsd : shown.priceMicroUsd;
}

export const HIDDEN_AMOUNT = "—";

/** "$100.00", "12,000 tokens", "500 requests"; a dash for a figure hidden from this viewer. */
export function amountText(metric: QuotaMetric, amount: number | null): string {
  if (amount === null) return HIDDEN_AMOUNT;
  if (metric === "spend") return formatUsd(amount);
  return `${amount.toLocaleString("en-US")} ${metric}`;
}

/** The amount of a quota (or grant) in its metric, after the money policy. */
export function shownAmount(policy: MoneyPolicy, quota: Pick<Quota, "metric" | "basis">, amount: number | null): string {
  return amountText(quota.metric, visibleAmount(policy, quota.metric, quota.basis, amount));
}

/** A spend input in dollars (`100`, `$1,250.50`) or a count (`12000`, `12,000`) as the integer the API takes. */
export function parseAmount(metric: QuotaMetric, input: string): number | null {
  const text = input.trim();
  if (metric === "spend") {
    const micro = parseUsd(text);
    if (micro === null || micro < 1n || micro > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    return Number(micro);
  }
  if (!/^\d{1,3}(,\d{3})*$|^\d+$/.test(text)) return null;
  const count = Number(text.replace(/,/g, ""));
  return Number.isSafeInteger(count) && count >= 1 ? count : null;
}

/** The text of an amount input: dollars for spend (`100`, `0.5`), the count otherwise. */
export function amountInput(metric: QuotaMetric, amount: number | null): string {
  if (amount === null) return "";
  if (metric !== "spend") return String(amount);
  return formatUsd(amount, 6).replace(/^\$/, "").replace(/,/g, "").replace(/\.?0+$/, "");
}

function amountProblem(metric: QuotaMetric): string {
  return metric === "spend" ? "A dollar amount above zero, at most six decimals." : "A whole number of at least 1.";
}

// ---------------------------------------------------------------------------------------------
// Who may write what (API-230): org-owned quotas are the org's admins' (and a super-admin's),
// platform-owned guardrails the super-admin's or a billing:write platform principal's; a
// deployment-pinned quota (`source = installation`) is nobody's.

export interface WriteAccess {
  org: boolean;
  platform: boolean;
}

export function writeAccess(me: ScopeMe): WriteAccess {
  if (me.role === "super-admin") return { org: true, platform: true };
  if (me.role === "platform") return { org: false, platform: me.permissions.includes("billing:write") };
  return { org: true, platform: false };
}

/** Why the quota is read-only to this viewer, or null when they may change it. */
export function readOnlyReason(quota: Pick<Quota, "owner" | "source">, access: WriteAccess): string | null {
  if (quota.source === "installation") return "Set by a deployment's quota pin; change it in the deployment, not here.";
  if (quota.owner === "platform" && !access.platform) return "A guardrail AllCode set on this organization; only AllCode can change it.";
  if (quota.owner === "org" && !access.org) return "Your role may change only AllCode's guardrail quotas.";
  return null;
}

export function canCreate(access: WriteAccess): boolean {
  return access.org || access.platform;
}

// ---------------------------------------------------------------------------------------------
// The list (SCR-230), grouped by scope

/** "Each member of team Platform", "Dana Lee", "Whole organization". */
export function quotaScopeText(quota: Pick<Quota, "scope" | "teamName" | "displayName" | "email" | "userExternalId">): string {
  const team = quota.teamName?.trim();
  switch (quota.scope) {
    case "team":
      return team ? `Team ${team}` : UNKNOWN_TEAM;
    case "team_member":
      return `Each member of ${team ? `team ${team}` : `an ${UNKNOWN_TEAM.toLowerCase()}`}`;
    case "user":
      return personName({ displayName: quota.displayName, email: quota.email, externalId: quota.userExternalId });
    default:
      return scopeLabel(quota.scope);
  }
}

export interface QuotaRow {
  id: string;
  name: string;
  scope: QuotaScope;
  scopeText: string;
  metric: string;
  period: string;
  limit: string;
  enforcement: string;
  failMode: string;
  statusLabel: string;
  statusTone: ChipTone;
  ownerLabel: string;
  ownerTone: ChipTone;
  /** `From installation` for a deployment-pinned quota. */
  sourceLabel: string | null;
  warnings: readonly string[];
  readOnly: string | null;
  quota: Quota;
}

const WARNING_TEXT: Readonly<Record<Schemas["QuotaWarning"], string>> = {
  exceeds_parent: "Larger than its parent's cap, which still bounds it.",
  unresolved_target: "Names a team or person the organization does not have yet.",
};

export function quotaRow(quota: Quota, policy: MoneyPolicy, access: WriteAccess): QuotaRow {
  const basis = quota.metric === "spend" ? ` (${quota.basis === "list" ? "AWS list cost" : "price"})` : "";
  return {
    id: quota.id,
    name: quota.name,
    scope: quota.scope,
    scopeText: quotaScopeText(quota),
    metric: `${metricLabel(quota.metric)}${basis}`,
    period: periodLabel(quota.period),
    limit: shownAmount(policy, quota, quota.limit),
    enforcement: quota.enforcement === "block" ? "Block" : "Warn only",
    failMode: quota.failMode === "open" ? "Admit" : "Refuse",
    statusLabel: quota.status === "active" ? "Active" : "Disabled",
    statusTone: quota.status === "active" ? "green" : "grey",
    ownerLabel: quota.owner === "platform" ? "Guardrail" : "Organization",
    ownerTone: quota.owner === "platform" ? "blue" : "grey",
    sourceLabel: quota.source === "installation" ? "From installation" : null,
    warnings: quota.warnings.map((warning) => WARNING_TEXT[warning]),
    readOnly: readOnlyReason(quota, access),
    quota,
  };
}

/** Org first, then teams, members and people (SCR-230's grouping); by name within a scope. */
export function quotaRows(quotas: readonly Quota[], policy: MoneyPolicy, access: WriteAccess): QuotaRow[] {
  return quotas
    .map((quota) => quotaRow(quota, policy, access))
    .sort((a, b) => SCOPES.indexOf(a.scope) - SCOPES.indexOf(b.scope) || a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------------------------
// The editor's form (SCR-230): strings as typed, validated against the spec's bounds

export const LIMITS = {
  name: 100,
  thresholds: 5,
  thresholdMin: 1,
  thresholdMax: 9999,
  graceMax: 5000,
  reasonMin: 10,
  reasonMax: 500,
  grantDays: 31,
} as const;

export const DEFAULT_THRESHOLDS_BP: readonly number[] = [7500, 9500];

export interface QuotaFormValues {
  name: string;
  scope: QuotaScope;
  teamId: string;
  userId: string;
  metric: QuotaMetric;
  basis: QuotaBasis;
  period: QuotaPeriod;
  /** Dollars for spend, a count otherwise. */
  limit: string;
  /** Percentages, comma separated: "75, 95". */
  thresholds: string;
  enforcement: QuotaEnforcement;
  failMode: QuotaFailMode;
  /** A percentage past the limit: "0", "10". */
  grace: string;
  owner: QuotaOwner;
}

export type EditorMode = { kind: "create" } | { kind: "edit"; quota: Quota };

export function thresholdsInput(bps: readonly number[]): string {
  return bps.map(bpToPercentInput).join(", ");
}

/** A new quota: the spec's defaults, owned by whoever may write org quotas (else a guardrail). */
export function blankQuotaForm(access: WriteAccess): QuotaFormValues {
  return {
    name: "",
    scope: "team_member",
    teamId: "",
    userId: "",
    metric: "spend",
    basis: "price",
    period: "month",
    limit: "",
    thresholds: thresholdsInput(DEFAULT_THRESHOLDS_BP),
    enforcement: "block",
    failMode: "open",
    grace: "0",
    owner: access.org ? "org" : "platform",
  };
}

export function formFromQuota(quota: Quota): QuotaFormValues {
  return {
    name: quota.name,
    scope: quota.scope,
    teamId: quota.teamId ?? "",
    userId: quota.userId ?? "",
    metric: quota.metric,
    basis: quota.basis ?? "price",
    period: quota.period,
    limit: amountInput(quota.metric, quota.limit),
    thresholds: thresholdsInput(quota.thresholdsBp),
    enforcement: quota.enforcement,
    failMode: quota.failMode,
    grace: bpToPercentInput(quota.graceBp),
    owner: quota.owner,
  };
}

/** The thresholds as basis points, or the problem with them. */
export function parseThresholds(input: string): { bp: number[] } | { error: string } {
  const parts = input.split(/[,\s]+/).filter((part) => part !== "");
  if (parts.length > LIMITS.thresholds) return { error: `At most ${LIMITS.thresholds} thresholds.` };
  const bp: number[] = [];
  for (const part of parts) {
    const value = parsePercentToBp(part);
    if (value === null || value < LIMITS.thresholdMin || value > LIMITS.thresholdMax) return { error: "Each threshold is a percentage from 0.01 to 99.99." };
    if (bp.length > 0 && value <= (bp[bp.length - 1] as number)) return { error: "Thresholds go up from left to right, each once." };
    bp.push(value);
  }
  return { bp };
}

export function parseGrace(input: string): number | null {
  const bp = parsePercentToBp(input.trim() === "" ? "0" : input);
  return bp === null || bp < 0 || bp > LIMITS.graceMax ? null : bp;
}

export interface ValidationContext {
  mode: EditorMode;
  surface: Surface;
  access: WriteAccess;
}

/** The form's problems by field, mirroring the spec's bounds and the domain rules it encodes. */
export function validateQuotaForm(values: QuotaFormValues, context: ValidationContext): FieldErrors {
  const errors: FieldErrors = {};
  const name = [...values.name.trim()].length;
  if (name === 0) errors["name"] = "Required.";
  else if (name > LIMITS.name) errors["name"] = `At most ${LIMITS.name} characters.`;

  const creating = context.mode.kind === "create";
  if (creating) {
    const target = scopeTarget(values.scope);
    if (target === "team" && values.teamId === "") errors["teamId"] = "Pick the team.";
    if (target === "user" && values.userId === "") errors["userId"] = "Pick the person.";
    if (values.owner === "platform" && (context.surface !== "staff" || !context.access.platform)) errors["owner"] = "Only AllCode sets guardrail quotas.";
    if (values.owner === "org" && !context.access.org) errors["owner"] = "Your role sets guardrail quotas only.";
    if (values.metric === "spend" && values.basis === "list" && values.owner !== "platform") errors["basis"] = "AWS list cost is for guardrail quotas only.";
  }

  const hiddenUnchanged = context.mode.kind === "edit" && context.mode.quota.limit === null && values.limit.trim() === "";
  if (!hiddenUnchanged && parseAmount(values.metric, values.limit) === null) errors["limit"] = values.limit.trim() === "" ? "Required." : amountProblem(values.metric);

  const thresholds = parseThresholds(values.thresholds);
  if ("error" in thresholds) errors["thresholds"] = thresholds.error;
  if (parseGrace(values.grace) === null) errors["grace"] = "A percentage from 0 to 50.";
  return errors;
}

function thresholdsOf(values: QuotaFormValues): number[] {
  const parsed = parseThresholds(values.thresholds);
  return "bp" in parsed ? parsed.bp : [];
}

/** The body of `createQuota` and `previewQuota` (API-230, API-237); call only on a valid form. */
export function toDraft(values: QuotaFormValues): QuotaDraft {
  const target = scopeTarget(values.scope);
  const draft: QuotaDraft = {
    name: values.name.trim(),
    scope: values.scope,
    metric: values.metric,
    period: values.period,
    limit: parseAmount(values.metric, values.limit) ?? 0,
    thresholdsBp: thresholdsOf(values),
    enforcement: values.enforcement,
    graceBp: parseGrace(values.grace) ?? 0,
    failMode: values.failMode,
    owner: values.owner,
  };
  if (target === "team") draft.teamId = values.teamId;
  if (target === "user") draft.userId = values.userId;
  if (values.metric === "spend") draft.basis = values.basis;
  return draft;
}

/**
 * The preview request: the draft as it would be created; an edit previews the quota's identity
 * (scope, metric, basis, period, owner — which a patch cannot change) with the edited fields.
 */
export function previewDraft(values: QuotaFormValues, mode: EditorMode): QuotaDraft {
  if (mode.kind === "create") return toDraft(values);
  return toDraft({ ...formFromQuota(mode.quota), ...pickEditable(values) });
}

/** Preview needs a valid form with a limit it can send (a hidden limit left blank has none). */
export function canPreview(values: QuotaFormValues, errors: FieldErrors): boolean {
  return Object.keys(errors).length === 0 && parseAmount(values.metric, values.limit) !== null;
}

function pickEditable(values: QuotaFormValues): Pick<QuotaFormValues, "name" | "limit" | "thresholds" | "enforcement" | "failMode" | "grace"> {
  return { name: values.name, limit: values.limit, thresholds: values.thresholds, enforcement: values.enforcement, failMode: values.failMode, grace: values.grace };
}

/** Only what changed, with the version last read (API-230's optimistic update). */
export function toPatch(values: QuotaFormValues, quota: Quota): PatchQuotaRequest {
  const patch: PatchQuotaRequest = { version: quota.version };
  const name = values.name.trim();
  if (name !== quota.name) patch.name = name;
  const limit = parseAmount(quota.metric, values.limit);
  if (limit !== null && limit !== quota.limit) patch.limit = limit;
  const thresholds = thresholdsOf(values);
  if (thresholds.join(",") !== quota.thresholdsBp.join(",")) patch.thresholdsBp = thresholds;
  if (values.enforcement !== quota.enforcement) patch.enforcement = values.enforcement;
  const grace = parseGrace(values.grace) ?? quota.graceBp;
  if (grace !== quota.graceBp) patch.graceBp = grace;
  if (values.failMode !== quota.failMode) patch.failMode = values.failMode;
  return patch;
}

/** Whether a patch changes anything beyond its version. */
export function patchChanges(patch: PatchQuotaRequest): boolean {
  return Object.keys(patch).length > 1;
}

// ---------------------------------------------------------------------------------------------
// Preview (REQ-094, API-237)

export interface PreviewRow {
  key: string;
  scopeName: string;
  consumed: string;
  thresholds: readonly { label: string; reachedOn: string | null }[];
  limitReachedOn: string | null;
}

export interface PreviewSummary {
  period: string;
  instances: number;
  /** "3 of 12 would have reached 75%". */
  lines: readonly string[];
  rows: readonly PreviewRow[];
  unpriced: string | null;
}

function day(iso: string): string {
  return iso.slice(0, 10);
}

export function previewSummary(preview: QuotaPreview, policy: MoneyPolicy): PreviewSummary {
  const instances = preview.items.length;
  const thresholdBps = [...new Set(preview.items.flatMap((item) => item.thresholds.map((t) => t.thresholdBp)))].sort((a, b) => a - b);
  const reached = (bp: number) => preview.items.filter((item) => item.thresholds.some((t) => t.thresholdBp === bp && t.reachedOn !== null)).length;
  const atLimit = preview.items.filter((item) => item.limitReachedOn !== null).length;
  const lines = [
    ...thresholdBps.map((bp) => `${reached(bp)} of ${instances} would have reached ${bpPercent(bp)}.`),
    `${atLimit} of ${instances} would have reached the limit${atLimit > 0 ? " and been refused" : ""}.`,
  ];
  const end = new Date(Date.parse(preview.periodEnd) - 1).toISOString();
  return {
    period: `${day(preview.periodStart)} to ${day(end)} (UTC)`,
    instances,
    lines,
    rows: preview.items.map((item, index) => ({
      key: item.scopeId ?? `org-${index}`,
      scopeName: item.scopeName,
      consumed: amountText(preview.metric, visibleAmount(policy, preview.metric, preview.basis, item.consumed)),
      thresholds: item.thresholds.map((t) => ({ label: bpPercent(t.thresholdBp), reachedOn: t.reachedOn })),
      limitReachedOn: item.limitReachedOn,
    })),
    unpriced: preview.unpricedModels.length > 0 ? `Counted at $0, no price yet: ${preview.unpricedModels.join(", ")}.` : null,
  };
}

// ---------------------------------------------------------------------------------------------
// Standing (SCR-231, API-232)

/** The counters are stale when their newest hour is more than ten minutes old. */
export const STALE_AFTER_MS = 10 * 60 * 1000;

export function isStale(asOf: string, now: Date): boolean {
  return now.getTime() - Date.parse(asOf) > STALE_AFTER_MS;
}

export interface StandingRow {
  key: string;
  quotaId: string;
  quotaName: string;
  scopeName: string;
  /** "$42.00 of $100.00"; null when money is hidden, so only the % shows. */
  amounts: string | null;
  percent: string;
  /** 0..1 for the bar; a quota past its limit fills it. */
  fraction: number;
  stateLabel: string;
  stateTone: ChipTone;
  resets: string;
  grants: number;
  refused: readonly string[];
}

/** "1 Oct, 00:00 UTC". */
export function utcMoment(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const month = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${date.getUTCDate()} ${month} ${date.getUTCFullYear()}, ${iso.slice(11, 16)} UTC`;
}

export function standingRow(item: QuotaStanding, policy: MoneyPolicy): StandingRow {
  const consumed = visibleAmount(policy, item.metric, item.basis, item.consumed);
  const limit = visibleAmount(policy, item.metric, item.basis, item.effectiveLimit);
  return {
    key: `${item.quotaId}:${item.scopeId ?? "org"}`,
    quotaId: item.quotaId,
    quotaName: item.name,
    scopeName: item.scopeName,
    amounts: consumed === null || limit === null ? null : `${amountText(item.metric, consumed)} of ${amountText(item.metric, limit)}`,
    percent: bpPercentShort(item.utilizationBp),
    fraction: Math.min(Math.max(item.utilizationBp, 0), 10000) / 10000,
    stateLabel: stateLabel(item.state),
    stateTone: stateTone(item.state),
    resets: utcMoment(item.resetsAt),
    grants: item.activeGrants,
    refused: item.refusedUsers.map((user) => personName({ displayName: user.displayName, email: user.email, externalId: user.userExternalId })),
  };
}

/** Utilization for display, truncated to a whole percent; "≥ 1000%" for the saturated far end. */
export function bpPercentShort(bp: number): string {
  if (bp >= 100_000) return "≥ 1000%";
  return `${Math.trunc(bp / 100)}%`;
}

/** Fullest first, so whatever is nearest refusing sits at the top. */
export function standingRows(response: QuotaStandingResponse, policy: MoneyPolicy): StandingRow[] {
  return [...response.items].sort((a, b) => b.utilizationBp - a.utilizationBp).map((item) => standingRow(item, policy));
}

// ---------------------------------------------------------------------------------------------
// Events (ENT-234, API-232)

export const EVENTS_PAGE = 100;

export interface EventRow {
  key: string;
  when: string;
  quotaName: string;
  what: string;
  tone: ChipTone;
  who: string;
  amounts: string | null;
}

export function eventWhat(event: Pick<QuotaEvent, "kind" | "thresholdBp">): { text: string; tone: ChipTone } {
  if (event.kind === "threshold") return { text: `Crossed ${bpPercent(event.thresholdBp ?? 0)}`, tone: "amber" };
  if (event.kind === "exhausted") return { text: "Limit reached, refusing", tone: "red" };
  return { text: "Grant added", tone: "blue" };
}

/** The scope instance an event is about: the member, the team, or the org. */
export function eventWho(event: Pick<QuotaEvent, "scope" | "scopeId" | "scopeName" | "userId" | "displayName" | "email" | "userExternalId">): string {
  if (event.userId) return personName({ displayName: event.displayName, email: event.email, externalId: event.userExternalId });
  if (event.scope === "user" && event.scopeId) return event.scopeName?.trim() || UNKNOWN_USER;
  if (event.scopeId) return teamName(event.scopeName);
  return "Whole organization";
}

export function eventRows(events: readonly QuotaEvent[], quotas: ReadonlyMap<string, Pick<Quota, "metric" | "basis">>, policy: MoneyPolicy): EventRow[] {
  return events.map((event) => {
    const quota = quotas.get(event.quotaId);
    const what = eventWhat(event);
    const shown = (amount: number | null) => (quota ? visibleAmount(policy, quota.metric, quota.basis, amount) : null);
    const consumed = shown(event.consumed);
    const limit = shown(event.limit);
    return {
      key: String(event.id),
      when: utcMoment(event.occurredAt),
      quotaName: event.quotaName,
      what: what.text,
      tone: what.tone,
      who: eventWho(event),
      amounts: quota && consumed !== null && limit !== null ? `${amountText(quota.metric, consumed)} of ${amountText(quota.metric, limit)}` : null,
    };
  });
}

// ---------------------------------------------------------------------------------------------
// Grants (REQ-082, API-231)

export type GrantStatus = "active" | "scheduled" | "expired" | "revoked";

export function grantStatus(grant: Pick<QuotaGrant, "startsAt" | "expiresAt" | "revokedAt">, now: Date): GrantStatus {
  if (grant.revokedAt !== null) return "revoked";
  if (Date.parse(grant.expiresAt) <= now.getTime()) return "expired";
  return Date.parse(grant.startsAt) > now.getTime() ? "scheduled" : "active";
}

const GRANT_STATUS: Readonly<Record<GrantStatus, { label: string; tone: ChipTone }>> = {
  active: { label: "Active", tone: "green" },
  scheduled: { label: "Scheduled", tone: "blue" },
  expired: { label: "Expired", tone: "grey" },
  revoked: { label: "Revoked", tone: "grey" },
};

export interface GrantRow {
  id: string;
  what: string;
  who: string;
  reason: string;
  expires: string;
  statusLabel: string;
  statusTone: ChipTone;
  revocable: boolean;
  grant: QuotaGrant;
}

export function grantRows(grants: readonly QuotaGrant[], quota: Pick<Quota, "metric" | "basis">, policy: MoneyPolicy, now: Date): GrantRow[] {
  return grants.map((grant) => {
    const status = grantStatus(grant, now);
    return {
      id: grant.id,
      what: grant.kind === "exempt" ? "Exempt" : `+ ${shownAmount(policy, quota, grant.amount)}`,
      who: grant.userId ? personName({ displayName: grant.displayName, email: grant.email, externalId: grant.userExternalId }) : "Whole scope",
      reason: grant.reason,
      expires: utcMoment(grant.expiresAt),
      statusLabel: GRANT_STATUS[status].label,
      statusTone: GRANT_STATUS[status].tone,
      revocable: status === "active" || status === "scheduled",
      grant,
    };
  });
}

export interface GrantFormValues {
  kind: QuotaGrantKind;
  amount: string;
  /** "" = the whole scope. */
  userId: string;
  reason: string;
  /** Whole days from now, 1..31. */
  days: string;
}

export function blankGrantForm(): GrantFormValues {
  return { kind: "increase", amount: "", userId: "", reason: "", days: "3" };
}

function parseDays(input: string): number | null {
  const text = input.trim();
  if (!/^\d+$/.test(text)) return null;
  const days = Number(text);
  return days >= 1 && days <= LIMITS.grantDays ? days : null;
}

export function validateGrantForm(values: GrantFormValues, quota: Pick<Quota, "metric" | "scope">): FieldErrors {
  const errors: FieldErrors = {};
  if (values.kind === "increase" && parseAmount(quota.metric, values.amount) === null) errors["amount"] = values.amount.trim() === "" ? "Required." : amountProblem(quota.metric);
  if (values.userId !== "" && !isMemberScope(quota.scope)) errors["userId"] = "Only a per-member quota takes one person.";
  const reason = [...values.reason.trim()].length;
  if (reason < LIMITS.reasonMin || reason > LIMITS.reasonMax) errors["reason"] = `Between ${LIMITS.reasonMin} and ${LIMITS.reasonMax} characters.`;
  if (parseDays(values.days) === null) errors["days"] = `Whole days from 1 to ${LIMITS.grantDays}.`;
  return errors;
}

/**
 * Who a grant's "For" picker offers: a team's per-member quota only that team's members (the API
 * refuses anyone else), an org's per-member quota everyone; a quota without members, no one.
 */
export function grantMembers<U extends Pick<User, "teamId">>(quota: Pick<Quota, "scope" | "teamId">, users: readonly U[]): U[] {
  if (quota.scope === "org_member") return [...users];
  if (quota.scope === "team_member") return users.filter((user) => user.teamId === quota.teamId);
  return [];
}

/** The grant's end: now plus the days, in UTC. */
export function grantExpiry(now: Date, days: string): string {
  return new Date(now.getTime() + (parseDays(days) ?? 0) * 86_400_000).toISOString();
}

/** The body of `createQuotaGrant`; call only on a valid form. */
export function toGrantRequest(values: GrantFormValues, quota: Pick<Quota, "metric">, now: Date): CreateQuotaGrantRequest {
  const request: CreateQuotaGrantRequest = { kind: values.kind, reason: values.reason.trim(), expiresAt: grantExpiry(now, values.days) };
  if (values.kind === "increase") request.amount = parseAmount(quota.metric, values.amount) ?? 0;
  if (values.userId !== "") request.userId = values.userId;
  return request;
}

// ---------------------------------------------------------------------------------------------
// Errors and the operations not served yet

/** The API answers 501 `not_implemented` for these operations until their handlers land. */
export function isNotAvailable(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 501 || error.code === "not_implemented");
}

export const NOT_AVAILABLE = "Quotas are not available yet on this Nexus API. They appear here once the server is updated.";

export function asApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

export function isVersionConflict(error: unknown): boolean {
  return error instanceof ApiError && error.code === "version_conflict";
}

/** A sentence for a refused write; field-level `invalid_quota` details show beside the fields. */
export function writeFailure(error: ApiError): string {
  switch (error.code) {
    case "version_conflict":
      return "Someone changed this quota after you opened it. Reload it to see their change, then edit again.";
    case "quota_exists":
      return "An active quota already caps this scope with the same metric and period. Edit that one instead.";
    case "quota_read_only":
      return "This quota is pinned by a deployment and cannot be changed here.";
    case "grant_revoked":
      return "This grant was already revoked.";
    case "forbidden":
      return "Your role cannot make this change.";
    default:
      return isNotAvailable(error) ? NOT_AVAILABLE : error.message;
  }
}

export type Loadable<T> = { kind: "loading" } | { kind: "unavailable" } | { kind: "error"; error: ApiError } | { kind: "loaded"; value: T };

/** A read as the screen's state: a 501 is "not available yet", never an error banner or a crash. */
export async function loadable<T>(read: () => Promise<T>): Promise<Loadable<T>> {
  try {
    return { kind: "loaded", value: await read() };
  } catch (error) {
    if (isNotAvailable(error)) return { kind: "unavailable" };
    return { kind: "error", error: asApiError(error) };
  }
}

// ---------------------------------------------------------------------------------------------
// Reads and writes (API-230..232, API-237)

export const TARGET_PAGE = 500;

export interface Targets {
  teams: Team[];
  users: User[];
}

/** The org's teams and people for the scope picker (the first page of each, as the users tab). */
export async function loadTargets(client: ApiClient, orgId: string): Promise<Targets> {
  const [teams, users] = await Promise.all([
    client.get("/v1/orgs/{orgId}/teams", { path: { orgId }, query: { limit: TARGET_PAGE } }),
    client.get("/v1/orgs/{orgId}/users", { path: { orgId }, query: { limit: TARGET_PAGE } }),
  ]);
  return { teams: teams.items, users: users.items };
}

export async function loadQuotas(client: ApiClient, orgId: string): Promise<Quota[]> {
  return (await client.get("/v1/orgs/{orgId}/quotas", { path: { orgId } })).items;
}

export function loadQuota(client: ApiClient, orgId: string, quotaId: string): Promise<Quota> {
  return client.get("/v1/orgs/{orgId}/quotas/{quotaId}", { path: { orgId, quotaId } });
}

export function createQuota(client: ApiClient, orgId: string, draft: QuotaDraft): Promise<Quota> {
  return client.post("/v1/orgs/{orgId}/quotas", { path: { orgId }, body: draft });
}

export function patchQuota(client: ApiClient, orgId: string, quotaId: string, patch: PatchQuotaRequest): Promise<Quota> {
  return client.patch("/v1/orgs/{orgId}/quotas/{quotaId}", { path: { orgId, quotaId }, body: patch });
}

/** Disable an active quota or enable a disabled one. */
export function toggleStatus(client: ApiClient, quota: Quota): Promise<Quota> {
  const status = quota.status === "active" ? "disabled" : "active";
  return patchQuota(client, quota.orgId, quota.id, { version: quota.version, status });
}

/** Delete with the version last read in `If-Match` (correction 29); a stale one is `version_conflict`. */
export async function deleteQuota(client: ApiClient, quota: Quota): Promise<void> {
  await client.delete("/v1/orgs/{orgId}/quotas/{quotaId}", { path: { orgId: quota.orgId, quotaId: quota.id }, headers: { "If-Match": String(quota.version) } });
}

export function previewQuota(client: ApiClient, orgId: string, draft: QuotaDraft, signal?: AbortSignal): Promise<QuotaPreview> {
  return client.post("/v1/orgs/{orgId}/quota-preview", signal ? { path: { orgId }, body: draft, signal } : { path: { orgId }, body: draft });
}

export async function loadGrants(client: ApiClient, orgId: string, quotaId: string): Promise<QuotaGrant[]> {
  return (await client.get("/v1/orgs/{orgId}/quotas/{quotaId}/grants", { path: { orgId, quotaId } })).items;
}

export function createGrant(client: ApiClient, orgId: string, quotaId: string, request: CreateQuotaGrantRequest): Promise<QuotaGrant> {
  return client.post("/v1/orgs/{orgId}/quotas/{quotaId}/grants", { path: { orgId, quotaId }, body: request });
}

export function revokeGrant(client: ApiClient, grant: QuotaGrant): Promise<QuotaGrant> {
  return client.post("/v1/orgs/{orgId}/quotas/{quotaId}/grants/{grantId}/revoke", { path: { orgId: grant.orgId, quotaId: grant.quotaId, grantId: grant.id } });
}

export function loadStanding(client: ApiClient, orgId: string, quotaId?: string): Promise<QuotaStandingResponse> {
  return client.get("/v1/orgs/{orgId}/quota-standing", quotaId ? { path: { orgId }, query: { quotaId } } : { path: { orgId } });
}

export interface EventsQuery {
  quotaId?: string | undefined;
  cursor?: string | null | undefined;
}

export function eventsQuery({ quotaId, cursor }: EventsQuery): { limit: number; quotaId?: string; cursor?: string } {
  const query: { limit: number; quotaId?: string; cursor?: string } = { limit: EVENTS_PAGE };
  if (quotaId) query.quotaId = quotaId;
  if (cursor) query.cursor = cursor;
  return query;
}

export function loadEvents(client: ApiClient, orgId: string, query: EventsQuery): Promise<QuotaEventList> {
  return client.get("/v1/orgs/{orgId}/quota-events", { path: { orgId }, query: eventsQuery(query) });
}
